
import React, { useRef, useState, useEffect } from 'react';
import { Card, Button } from '../components/UI';
import { Download, Monitor, Grid, Trophy, Calendar, BarChart2, Flag, List, Timer, Activity, RefreshCw, X, User, Image as ImageIcon, Layers, Swords, FileText, Maximize, Minimize, TrendingUp } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { apiRequest } from '../utils/api';
import { useLeague } from '../components/LeagueContext';

type GraphicType = 'DRIVER_STANDINGS' | 'TEAM_STANDINGS' | 'RACE_RESULTS' | 'RACE_RESULTS_DETAILED' | 'QUALI_RESULTS' | 'STARTING_GRID' | 'CALENDAR' | 'SEASON_STATS' | 'HEAD_TO_HEAD' | 'TEAMMATE_BATTLE' | 'DRIVER_STATS' | 'LEAGUE_STATS' | 'DRIVER_CHART' | 'TEAM_CHART';
type H2HMode = 'DRIVER' | 'TEAM';

const THEME_COLORS = {
    cyan: '#00f0d0',
    black: '#000000',
    dark: '#15151e',
    white: '#ffffff',
    gray: '#f3f3f3',
    cardBg: '#1f1f27',
    textDark: '#1f1f27',
    textLight: '#ffffff'
};

export const CasterGraphics: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { settings } = useLeague();
    
    // State
    const [activeType, setActiveType] = useState<GraphicType>('DRIVER_STANDINGS');
    const [isTransparent, setIsTransparent] = useState(false); // Checkbox state
    const [bgImage, setBgImage] = useState<string | null>(null); // Custom Background
    const [isFullscreen, setIsFullscreen] = useState(false);
    
    // H2H State
    const [h2hMode, setH2HMode] = useState<H2HMode>('DRIVER');
    const [h2hId1, setH2HId1] = useState<string>('');
    const [h2hId2, setH2HId2] = useState<string>('');
    
    // Single Driver Stats State
    const [selectedDriverId, setSelectedDriverId] = useState<string>('');
    const [driverStatsData, setDriverStatsData] = useState<any>(null);
    
    // Data State
    const [drivers, setDrivers] = useState<any[]>([]);
    const [teams, setTeams] = useState<any[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    const [results, setResults] = useState<any[]>([]);
    const [eventPenalties, setEventPenalties] = useState<any[]>([]);
    const [gridEntries, setGridEntries] = useState<any[]>([]);
    const [matrix, setMatrix] = useState<any>(null);
    const [leagueStatsData, setLeagueStatsData] = useState<any>(null);
    const [chartDataDriver, setChartDataDriver] = useState<any[]>([]);
    const [chartDataTeam, setChartDataTeam] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [selectedSession, setSelectedSession] = useState<'RACE' | 'SPRINT'>('RACE');
    const [loading, setLoading] = useState(false);

    // Initial Data Load
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [sData, tData, eData, mData, lData, cdDriver, cdTeam] = await Promise.all([
                apiRequest<any>('standings.php'),
                apiRequest<any[]>('teams.php'),
                apiRequest<any[]>('events.php'),
                apiRequest<any>('season_matrix.php'),
                apiRequest<any>('stats_rankings.php'),
                apiRequest<any[]>('chart_data.php?type=driver').catch(() => []),
                apiRequest<any[]>('chart_data.php?type=team').catch(() => [])
            ]);

            if (sData) {
                setDrivers(sData.drivers || []);
                setTeams(sData.constructors || []);
            }
            if (Array.isArray(tData)) {
                if (!sData || !sData.constructors || sData.constructors.length === 0) setTeams(tData);
            }
            if (Array.isArray(eData)) {
                const completed = eData.filter(e => e.status === 'COMPLETED');
                const upcoming = eData.filter(e => e.status !== 'COMPLETED');
                setEvents(eData);
                if (upcoming.length > 0) setSelectedEventId(upcoming[0].id);
                else if (completed.length > 0) setSelectedEventId(completed[completed.length - 1].id);
            }
            if (mData) setMatrix(mData);
            if (lData) setLeagueStatsData(lData);
            if (Array.isArray(cdDriver)) setChartDataDriver(cdDriver);
            if (Array.isArray(cdTeam)) setChartDataTeam(cdTeam);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Load specific results/grid when event changes
    useEffect(() => {
        const ev = events.find(e => e.id === selectedEventId);
        if (ev && !ev.isSprint) {
            setSelectedSession('RACE');
        }
    }, [selectedEventId, events]);

    useEffect(() => {
        if (selectedEventId) {
            setLoading(true);
            const promises = [];
            
            if (['RACE_RESULTS', 'QUALI_RESULTS', 'RACE_RESULTS_DETAILED'].includes(activeType)) {
                promises.push(
                    apiRequest<any[]>(`results.php?eventId=${selectedEventId}`)
                        .then(res => setResults(Array.isArray(res) ? res : []))
                        .catch(() => setResults([]))
                );
                
                if (activeType === 'RACE_RESULTS_DETAILED') {
                     promises.push(
                        apiRequest<any>(`penalties.php?eventId=${selectedEventId}`) 
                            .then(res => setEventPenalties(Array.isArray(res.data) ? res.data : []))
                            .catch(() => setEventPenalties([]))
                    );
                }
            }
            
            if (activeType === 'STARTING_GRID') {
                promises.push(
                    apiRequest<any>(`event_entries.php?eventId=${selectedEventId}`)
                        .then(res => setGridEntries(res.savedEntries || []))
                        .catch(() => setGridEntries([]))
                );
            }

            Promise.all(promises).finally(() => {
                setLoading(false);
            });
        }
    }, [selectedEventId, activeType]);
    
    // Load Driver Stats Data
    useEffect(() => {
        if (activeType === 'DRIVER_STATS' && selectedDriverId) {
            apiRequest(`driver_profile.php?id=${selectedDriverId}`)
                .then(res => setDriverStatsData(res))
                .catch(console.error);
        }
    }, [activeType, selectedDriverId]);

    // Fullscreen Change Listener
    useEffect(() => {
        const handleFsChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFsChange);
        return () => document.removeEventListener('fullscreenchange', handleFsChange);
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return `${baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
    };

    const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous"; 
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load ${url}`));
            img.src = getUrl(url) || "";
        });
    };
    
    const resolveTeamLogo = (item: any) => {
        if (!item) return null;
        return item.logoUrl || item.logo_url || item.teamLogo || null;
    };
    
    const resolveCarImage = (item: any) => {
        if (!item) return null;
        return item.carImageUrl || item.car_image_url || null;
    };

    const getContrastColor = (hexColor: string) => {
        if (!hexColor) return '#fff';
        const r = parseInt(hexColor.substr(1, 2), 16);
        const g = parseInt(hexColor.substr(3, 2), 16);
        const b = parseInt(hexColor.substr(5, 2), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? '#000000' : '#ffffff';
    };
    
    // Helper to draw rounded rects safely
    const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
        if (ctx.roundRect) {
            ctx.beginPath();
            ctx.roundRect(x, y, w, h, r);
            ctx.fill();
        } else {
            ctx.fillRect(x, y, w, h); // Fallback
        }
    };
    
    const parseTimeStr = (str: string): number => {
        if (!str || str === '-' || str === 'DNF' || str === 'DNS') return 0;
        try {
            const parts = str.split(':');
            if (parts.length === 2) {
                return (parseInt(parts[0]) * 60) + parseFloat(parts[1]);
            }
            return parseFloat(str) || 0;
        } catch { return 0; }
    };

    const drawCleanF1Background = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        ctx.fillStyle = '#f8f8f8';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#e0e0e0';
        for (let y = 0; y < h; y += 30) {
            for (let x = 0; x < w; x += 30) {
                if (x < 600 || x > w - 600) {
                    ctx.beginPath();
                    ctx.arc(x, y, 2, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        }
        ctx.fillStyle = '#000000';
        ctx.beginPath(); ctx.moveTo(w - 400, 0); ctx.lineTo(w, 0); ctx.lineTo(w, 400); ctx.lineTo(w - 150, 0); ctx.fill();
        ctx.beginPath(); ctx.moveTo(0, h); ctx.lineTo(400, h); ctx.lineTo(0, h - 400); ctx.fill();
        ctx.fillStyle = THEME_COLORS.cyan;
        ctx.fillRect(0, 120, w, 10);
        ctx.fillRect(0, h - 40, w, 10);
    };

    const drawHeader = (ctx: CanvasRenderingContext2D, title: string, subtitle: string, w: number, y = 100) => {
        ctx.fillStyle = '#000'; 
        ctx.font = '900 70px "Titillium Web"'; 
        ctx.textAlign = 'left'; 
        ctx.fillText(title, 100, y);
        
        ctx.fillStyle = THEME_COLORS.cyan;
        ctx.fillRect(100, y + 15, 600, 6);

        if (subtitle) {
            ctx.fillStyle = '#666'; 
            ctx.font = 'bold 30px "Titillium Web"';
            ctx.fillText(subtitle.toUpperCase(), 100, y + 50);
        }
    };

    // --- DRAWING FUNCTIONS ---

    const drawSeasonStats = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "SEASON INSIGHTS", "Performance Analytics", w);
        if (!matrix || !matrix.drivers) return;

        // Calculate Stats
        const stats = drivers.slice(0, 20).map(d => {
            const history = matrix.matrix[d.id] || {};
            let countRace = 0, totalQualiPos = 0, countQuali = 0, posGain = 0, podiums = 0;
            
            Object.values(history).forEach((r: any) => {
                const p = parseInt(r.position) || 0;
                
                // Use Grid Position for Start if available, else Quali
                let startPos = parseInt(r.grid_position) || 0;
                let qPos = parseInt(r.quali_position) || 0;
                
                if (startPos === 0 && qPos > 0) startPos = qPos;

                if (qPos > 0) { 
                    totalQualiPos += qPos; 
                    countQuali++; 
                }
                
                // FIXED: Count race as long as it's not DNS (includes DNF, DSQ)
                if (r.status !== 'DNS') {
                    countRace++;
                }
                
                if (r.status === 'FINISHED') {
                    if (p <= 3) podiums++;
                    // Gain: Start - Finish. E.g. Start 5, Finish 3 = 5 - 3 = +2
                    if (startPos > 0 && p > 0) {
                        posGain += (startPos - p);
                    }
                }
            });

            const ppr = countRace > 0 ? (parseFloat(d.totalPoints) / countRace).toFixed(2) : "0.0";
            const avgQuali = countQuali > 0 ? (totalQualiPos / countQuali).toFixed(1) : "-";
            return { ...d, races: countRace, avgQuali, podiums, posGain, ppr };
        }).sort((a:any, b:any) => parseFloat(b.totalPoints) - parseFloat(a.totalPoints));

        // Two Column Layout
        const startY = 180;
        const colW = 850;
        const rowH = 45;
        const gapX = 40;
        const gapY = 6;
        const leftX = 80;
        const rightX = leftX + colW + gapX;

        // Headers
        const drawColHeader = (bx: number) => {
            ctx.fillStyle = '#15151e';
            ctx.fillRect(bx, startY, colW, 40);
            ctx.fillStyle = '#aaa';
            ctx.font = 'bold 16px "Titillium Web"';
            ctx.textAlign = 'left';
            ctx.fillText("POS", bx + 20, startY + 27);
            ctx.fillText("DRIVER", bx + 70, startY + 27);
            
            ctx.textAlign = 'center';
            ctx.fillText("RACES", bx + 450, startY + 27);
            ctx.fillText("AVG Q.", bx + 550, startY + 27);
            ctx.fillText("PODS", bx + 650, startY + 27);
            ctx.fillText("+/-", bx + 730, startY + 27);
            ctx.fillText("PPR", bx + 810, startY + 27);
        };

        drawColHeader(leftX);
        drawColHeader(rightX);

        for (let i = 0; i < stats.length; i++) {
            if (i >= 20) break;
            const d = stats[i];
            const isLeft = i < 10;
            const bx = isLeft ? leftX : rightX;
            const rowIdx = i % 10;
            const y = startY + 45 + (rowIdx * (rowH + gapY));

            // Row BG
            ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.95)' : 'rgba(240,240,240,0.95)';
            roundRect(ctx, bx, y, colW, rowH, 4);

            // Pos
            ctx.fillStyle = '#000';
            ctx.font = '900 24px "Titillium Web"';
            ctx.textAlign = 'center';
            ctx.fillText((i+1).toString(), bx + 30, y + 32);

            // Team Strip
            ctx.fillStyle = d.teamColor || '#666';
            ctx.fillRect(bx + 55, y + 6, 5, rowH - 12);

            // Name
            ctx.fillStyle = '#111';
            ctx.font = 'bold 20px "Titillium Web"';
            ctx.textAlign = 'left';
            ctx.fillText(String(d.username).toUpperCase(), bx + 70, y + 32);

            // Stats Columns
            ctx.textAlign = 'center';
            ctx.font = '900 20px "Titillium Web"';
            
            // Races
            ctx.fillStyle = '#444';
            ctx.fillText(d.races.toString(), bx + 450, y + 32);
            
            // Avg Q
            ctx.fillText(d.avgQuali, bx + 550, y + 32);
            
            // Podiums
            ctx.fillStyle = d.podiums > 0 ? '#d4af37' : '#ccc';
            ctx.fillText(d.podiums.toString(), bx + 650, y + 32);
            
            // Gain
            const gain = d.posGain;
            const gainStr = gain > 0 ? `+${gain}` : gain.toString();
            ctx.fillStyle = gain > 0 ? '#10b981' : (gain < 0 ? '#ef4444' : '#888');
            ctx.fillText(gainStr, bx + 730, y + 32);
            
            // PPR
            ctx.fillStyle = '#1a1a1a';
            ctx.fillRect(bx + 770, y + 8, 80, rowH - 16);
            ctx.fillStyle = THEME_COLORS.cyan;
            ctx.font = 'bold 18px "Titillium Web"';
            ctx.fillText(d.ppr, bx + 810, y + 30);
        }
    };

    const drawDriverStandings = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "DRIVER STANDINGS", "World Championship", w);
        const startY = 180; const leftX = 100; const leftW = 950; const rowH_Big = 65; const gap_Big = 15;
        const rightX = 1100; const rightW = 720; const rowH_Small = 50; const gap_Small = 12;
        const data = drivers.slice(0, 22);
        
        for (let i = 0; i < data.length; i++) {
            const d = data[i]; const isTop10 = i < 10;
            const currentX = isTop10 ? leftX : rightX; const currentW = isTop10 ? leftW : rightW;
            const currentH = isTop10 ? rowH_Big : rowH_Small; const currentGap = isTop10 ? gap_Big : gap_Small;
            let currentY = startY + i * (currentH + currentGap);
            if (!isTop10) { const offsetIdx = i - 10; currentY = startY + offsetIdx * (currentH + currentGap); }
            const skew = 20;
            const rankW = isTop10 ? 80 : 60;

            ctx.fillStyle = i===0 ? THEME_COLORS.cyan : i<3 ? '#333' : '#ccc';
            ctx.beginPath(); ctx.moveTo(currentX + skew, currentY); ctx.lineTo(currentX + rankW + skew, currentY); ctx.lineTo(currentX + rankW, currentY + currentH); ctx.lineTo(currentX, currentY + currentH); ctx.fill();
            ctx.fillStyle = (i === 0) ? '#000' : (i < 3 ? '#fff' : '#000'); ctx.font = `900 ${isTop10 ? '36' : '28'}px "Titillium Web"`; ctx.textAlign = 'center'; ctx.fillText((i + 1).toString(), currentX + rankW/2 + skew/2, currentY + currentH * 0.7);
            
            const barX = currentX + rankW + (isTop10 ? 10 : 5); const fullBarW = currentW - rankW - (isTop10 ? 150 : 100);
            ctx.fillStyle = THEME_COLORS.cardBg; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + fullBarW + skew, currentY); ctx.lineTo(barX + fullBarW, currentY + currentH); ctx.lineTo(barX, currentY + currentH); ctx.fill();
            ctx.fillStyle = d.teamColor || '#666'; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + skew + 8, currentY); ctx.lineTo(barX + 8, currentY + currentH); ctx.lineTo(barX, currentY + currentH); ctx.fill();
            
            ctx.textAlign = 'left'; ctx.fillStyle = '#fff'; ctx.font = `bold ${isTop10 ? '32' : '24'}px "Titillium Web"`; ctx.fillText(String(d.username).toUpperCase(), barX + 30, currentY + currentH * 0.68);
            if (isTop10) { ctx.textAlign = 'right'; ctx.fillStyle = '#aaa'; ctx.font = 'bold 18px "Titillium Web"'; ctx.fillText(d.teamName?.toUpperCase() || "", barX + fullBarW - 20, currentY + currentH * 0.65); }
                
            const tLogo = resolveTeamLogo(d);
            if (tLogo) { try { const img = await loadImage(tLogo); const scale = (currentH * 0.6) / img.height; if (isTop10) ctx.drawImage(img, barX + (fullBarW/2) + 50, currentY + (currentH*0.2), img.width * scale, currentH * 0.6); else ctx.drawImage(img, barX + fullBarW - (img.width * scale) - 10, currentY + (currentH*0.2), img.width * scale, currentH * 0.6); } catch {} }

            const ptsX = barX + fullBarW + (isTop10 ? 10 : 5); const ptsW = isTop10 ? 120 : 80;
            ctx.fillStyle = '#000'; ctx.beginPath(); ctx.moveTo(ptsX + skew, currentY); ctx.lineTo(ptsX + ptsW + skew, currentY); ctx.lineTo(ptsX + ptsW, currentY + currentH); ctx.lineTo(ptsX, currentY + currentH); ctx.fill();
            ctx.fillStyle = THEME_COLORS.cyan; ctx.textAlign = 'center'; ctx.font = `900 ${isTop10 ? '36' : '28'}px "Titillium Web"`; ctx.fillText(Math.round(d.totalPoints).toString(), ptsX + ptsW/2 + skew/2, currentY + currentH * 0.7);
        }
    };
    
    const drawTeamStandings = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "CONSTRUCTORS", "World Championship", w);
        const startY = 180; const rowH = 75; const gap = 12;
        
        for (let i = 0; i < teams.length; i++) {
            const tData = teams[i]; const y = startY + i * (rowH + gap); const skew = 20;
            ctx.beginPath(); ctx.moveTo(100 + skew, y); ctx.lineTo(w - 300 + skew, y); ctx.lineTo(w - 300, y + rowH); ctx.lineTo(100, y + rowH); 
            ctx.fillStyle = '#1a1a1a'; ctx.fill();
            
            ctx.fillStyle = tData.teamColor || tData.color || '#fff'; ctx.fillRect(100, y + rowH - 6, w - 400, 6);
            ctx.fillStyle = '#fff'; ctx.font = '900 48px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText((i+1).toString(), 140, y + 55);
            
            let nameOffsetX = 220; 
            const logoUrl = resolveTeamLogo(tData);
            if (logoUrl) { try { const l = await loadImage(logoUrl); const s = Math.min(50 / l.width, 50 / l.height); ctx.drawImage(l, nameOffsetX, y + 12, l.width*s, l.height*s); nameOffsetX += (l.width*s) + 20; } catch {} }
            ctx.fillStyle = '#fff'; ctx.font = 'bold 42px "Titillium Web"'; ctx.fillText(tData.name.toUpperCase(), nameOffsetX, y + 52);
            
            const carImgUrl = resolveCarImage(tData);
            if(carImgUrl) { try { const car = await loadImage(carImgUrl); const maxH = rowH * 0.9; const ratio = maxH / car.height; const cW = car.width * ratio; const carX = w - 300 - cW - 20; ctx.save(); ctx.shadowColor = 'black'; ctx.shadowBlur = 5; ctx.drawImage(car, carX, y + (rowH-maxH)/2, cW, maxH); ctx.restore(); } catch{} }
            
            const ptsX = w - 280; ctx.fillStyle = THEME_COLORS.cyan; ctx.beginPath(); ctx.moveTo(ptsX + skew, y); ctx.lineTo(ptsX + 150 + skew, y); ctx.lineTo(ptsX + 150, y + rowH); ctx.lineTo(ptsX, y + rowH); ctx.fill();
            ctx.fillStyle = '#000'; ctx.textAlign = 'center'; ctx.font = '900 42px "Titillium Web"'; ctx.fillText(Math.round(tData.totalPoints || 0).toString(), ptsX + 75 + skew/2, y + 52);
        }
    };
    
    const drawStartingGrid = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const sub = ev ? `${ev.trackLocation.toUpperCase()} | ENTRY LIST` : "STARTING GRID";
        
        drawHeader(ctx, "STARTING GRID", sub, w);
        if (teams.length === 0) return;

        const startY = 160; const colCount = 2; const marginX = 100; const gapX = 60; const rowGap = 30; const colW = (w - (marginX * 2) - ((colCount - 1) * gapX)) / colCount; const rowH = 140; 
        
        for (let i = 0; i < teams.length; i++) {
            const team = teams[i]; const r = Math.floor(i / colCount); const c = i % colCount;
            const x = marginX + c * (colW + gapX); const y = startY + r * (rowH + rowGap);
            
            ctx.save();
            ctx.fillStyle = '#1e1e24'; ctx.beginPath(); ctx.moveTo(x + 20, y); ctx.lineTo(x + colW, y); ctx.lineTo(x + colW - 20, y + rowH); ctx.lineTo(x, y + rowH); ctx.fill(); 
            ctx.fillStyle = team.color || team.teamColor || '#333'; ctx.beginPath(); ctx.moveTo(x + 20, y); ctx.lineTo(x + 30, y); ctx.lineTo(x + 10, y + rowH); ctx.lineTo(x, y + rowH); ctx.fill();
            
            const teamDrivers = gridEntries.filter(d => d.team_id === team.id);
            const carUrl = resolveCarImage(team);
            if (carUrl) { try { const carImg = await loadImage(carUrl); const maxCarH = 90; const maxCarW = colW * 0.55; const ratio = Math.min(maxCarW / carImg.width, maxCarH / carImg.height); const drawCarW = carImg.width * ratio; const drawCarH = carImg.height * ratio; ctx.save(); ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 10; ctx.shadowOffsetY = 5; ctx.drawImage(carImg, x + colW - drawCarW - 20, y + rowH - drawCarH - 5, drawCarW, drawCarH); ctx.restore(); } catch {} }
            
            const logoUrl = resolveTeamLogo(team);
            if(logoUrl) { try { const l = await loadImage(logoUrl); const s = Math.min(60/l.width, 60/l.height); ctx.drawImage(l, x + colW - (l.width*s) - 30, y + 20, l.width*s, l.height*s); } catch{} }

            ctx.fillStyle = '#ffffff'; ctx.font = '900 32px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText(team.name.toUpperCase(), x + 45, y + 40);
            ctx.font = 'bold 24px "Titillium Web"';
            const d1 = teamDrivers[0]; const d2 = teamDrivers[1];
            if (d1) { ctx.fillStyle = (d1.is_reserve == 1 || d1.is_reserve === '1') ? '#eab308' : '#e0e0e0'; ctx.fillText(d1.username.toUpperCase(), x + 45, y + 80); } else { ctx.fillStyle = '#666'; ctx.fillText("TBA", x + 45, y + 80); }
            if (d2) { ctx.fillStyle = (d2.is_reserve == 1 || d2.is_reserve === '1') ? '#eab308' : '#e0e0e0'; ctx.fillText(d2.username.toUpperCase(), x + 45, y + 110); } else { ctx.fillStyle = '#666'; ctx.fillText("TBA", x + 45, y + 110); }
            ctx.restore();
        }
    };

    const drawRaceResults = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const title = ev ? `${ev.trackLocation.toUpperCase()}${selectedSession === 'SPRINT' ? ' SPRINT' : ''}` : "RACE RESULTS";
        drawHeader(ctx, title, "", w);
        if (!results || results.length === 0) return;
        
        const posField = selectedSession === 'SPRINT' ? 'sprint_position' : 'position';
        const statusField = selectedSession === 'SPRINT' ? 'sprint_status' : 'status';
        
        const filtered = results
            .filter(r => r[posField] > 0)
            .sort((a,b) => Number(a[posField]) - Number(b[posField]));

        if (filtered.length === 0) return;
        
        const podiumY = 150; const pW = 220; const pH = 320; const centerX = w / 2; const posOrder = [1, 0, 2]; const xOffsets = [-pW - 30, 0, pW + 30]; 
        
        for (let i = 0; i < 3; i++) {
            const rIdx = posOrder[i]; 
            if (rIdx >= filtered.length) continue; 
            const r = filtered[rIdx]; const x = centerX + xOffsets[i] - (pW/2); 
            const finalY = podiumY + [50, 0, 80][i];

            ctx.save();
            ctx.fillStyle = '#fff'; ctx.fillRect(x, finalY, pW, pH); ctx.strokeStyle = '#333'; ctx.lineWidth = 2; ctx.strokeRect(x, finalY, pW, pH);
            ctx.fillStyle = r.teamColor || '#333'; ctx.fillRect(x, finalY + pH - 10, pW, 10);
            
            const rLogo = resolveTeamLogo(r);
            if (rLogo) { try { const l = await loadImage(rLogo); const s = Math.min(80/l.width, 60/l.height); ctx.drawImage(l, x + (pW - l.width*s)/2, finalY + 140, l.width*s, l.height*s); } catch{} }
            
            ctx.fillStyle = '#000'; ctx.textAlign='center'; ctx.font = '900 120px "Titillium Web"'; ctx.fillText((rIdx+1).toString(), x+pW/2, finalY+130);
            ctx.font = 'bold 24px "Titillium Web"'; ctx.fillText(String(r.username).toUpperCase(), x+pW/2, finalY+240);
            ctx.restore();
        }
        
        const listStartY = 580; const listX = 100; const listW = w - 200; const listRowH = 40; const colGap = 40;
        const rest = filtered.slice(3); const mid = Math.ceil(rest.length / 2);
        
        ctx.save();
        for (let i = 0; i < rest.length; i++) {
            const r = rest[i]; const col = i < mid ? 0 : 1; const row = i % mid; 
            const colW = (listW / 2) - (colGap / 2); const lx = listX + (col * (colW + colGap)); 
            const finalLy = listStartY + (row * (listRowH + 6));

            const currentStatus = r[statusField] || 'FINISHED';

            ctx.fillStyle = '#fff'; ctx.fillRect(lx, finalLy, colW, listRowH);
            ctx.fillStyle = currentStatus === 'FINISHED' ? '#222' : '#aa0000'; ctx.fillRect(lx, finalLy, 50, listRowH);
            ctx.fillStyle = '#fff'; ctx.font = 'bold 20px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(currentStatus === 'FINISHED' ? (i + 4).toString() : currentStatus.substring(0,3), lx + 25, finalLy + 28);
            ctx.fillStyle = '#000'; ctx.textAlign = 'left'; ctx.fillText(String(r.username).toUpperCase(), lx + 60, finalLy + 28);
        }
        ctx.restore();
    };

    const drawDetailedRaceResults = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const trackName = ev ? ev.trackLocation.toUpperCase() : "GRAND PRIX";
        const sessionName = selectedSession === 'SPRINT' ? 'SPRINT CLASSIFICATION' : 'OFFICIAL CLASSIFICATION';
        
        ctx.save();
        ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, 150); 
        ctx.fillStyle = '#fff'; ctx.font = '900 60px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText(sessionName, 50, 80);
        ctx.fillStyle = '#999'; ctx.font = 'bold 30px "Titillium Web"'; ctx.fillText(trackName, 50, 130);
        ctx.fillStyle = THEME_COLORS.cyan; ctx.fillRect(0, 150, w, 10); ctx.fillRect(0, h-20, w, 20);
        ctx.restore();

        if (!results || results.length === 0) return;

        const posField = selectedSession === 'SPRINT' ? 'sprint_position' : 'position';
        const timeField = selectedSession === 'SPRINT' ? 'sprint_time' : 'time_diff';
        const statusField = selectedSession === 'SPRINT' ? 'sprint_status' : 'status';

        const filtered = results
            .filter(r => r[posField] > 0)
            .sort((a,b) => Number(a[posField]) - Number(b[posField]));

        if (filtered.length === 0) return;

        const marginX = 50; const colW = (w - (marginX * 3)) / 2; const startY = 200; const rowH = 65; const gap = 15; const maxRowsPerCol = 10; 

        for(let i=0; i<filtered.length; i++) {
            if (i >= 20) break;
            const r = filtered[i]; const colIndex = Math.floor(i / maxRowsPerCol); const rowIndex = i % maxRowsPerCol;
            const x = marginX + (colIndex * (colW + marginX)); const y = startY + (rowIndex * (rowH + gap));

            ctx.save();
            ctx.fillStyle = '#15151e'; ctx.fillRect(x, y, colW, rowH);
            
            const currentStatus = r[statusField] || 'FINISHED';
            const currentPos = Number(r[posField]);

            let posColor = '#333'; let posText = String(currentPos);
            if (currentStatus !== 'FINISHED') { posColor = '#333'; posText = "NC"; } 
            else {
                if (currentPos === 1) posColor = '#fff200';
                else if (currentPos === 2) posColor = '#dadada';
                else if (currentPos === 3) posColor = '#d45600';
                else if (currentPos <= 10) posColor = '#0090d0'; 
            }
            if (currentStatus !== 'FINISHED' && currentStatus !== 'DNS') { posColor = '#880000'; }

            ctx.fillStyle = posColor; ctx.fillRect(x, y, 50, rowH);
            ctx.fillStyle = (i===0 || i===1 || i===2 && currentStatus==='FINISHED') ? '#000' : '#fff';
            ctx.font = '900 28px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(posText, x + 25, y + 42);

            ctx.fillStyle = r.teamColor || '#666'; ctx.fillRect(x + 50, y, 6, rowH);

            const rLogo = resolveTeamLogo(r);
            if (rLogo) { try { const logo = await loadImage(rLogo); const s = 30 / logo.height; ctx.drawImage(logo, x + 65, y + 17, logo.width * s, 30); } catch {} }

            ctx.textAlign = 'left'; ctx.fillStyle = '#fff'; ctx.font = 'bold 24px "Titillium Web"'; ctx.fillText(String(r.username).toUpperCase(), x + 110, y + 42);
            
            const teamObj = teams.find(t => t.id === r.team_id || t.name === r.teamName);
            const carUrl = resolveCarImage(teamObj);
            if (carUrl) { try { const car = await loadImage(carUrl); const carH = rowH - 10; const carRatio = carH / car.height; const carW = car.width * carRatio; ctx.drawImage(car, x + 300, y + 5, carW, carH); } catch {} }

            ctx.textAlign = 'right'; ctx.font = 'mono 20px "Titillium Web"'; ctx.fillStyle = '#fff';
            let timeStr = r[timeField]; if (currentStatus !== 'FINISHED') timeStr = currentStatus; else if (i === 0) timeStr = r[timeField]; 
            ctx.fillText(timeStr || '-', x + colW - 60, y + 40);

            let penStr = "";
            const manualPens = eventPenalties.filter(p => p.user_id === r.user_id && p.time_penalty > 0);
            const manualSecs = manualPens.reduce((sum, p) => sum + parseInt(p.time_penalty), 0);
            if(manualSecs > 0) penStr = `+${manualSecs}s`;
             try { if (r.telemetry_data) { const t = typeof r.telemetry_data === 'string' ? JSON.parse(r.telemetry_data) : r.telemetry_data; if (t.penalties > 0 && !penStr) penStr = `+${t.penalties}s`; } } catch {}

            if (penStr) { ctx.fillStyle = '#ef4444'; ctx.font = 'bold 14px "Titillium Web"'; ctx.fillText(penStr, x + colW - 60, y + 18); }

            ctx.fillStyle = THEME_COLORS.cyan; ctx.font = 'bold 24px "Titillium Web"'; ctx.textAlign = 'center';
            
            // Points display logic
            let displayPoints = r.points || 0;
            if (selectedSession === 'SPRINT' && settings?.points_sprint) {
                displayPoints = settings.points_sprint[currentPos - 1] || 0;
            }

            if (displayPoints > 0) { ctx.fillText(String(displayPoints), x + colW - 25, y + 42); }

            let badgeX = x + colW - 180; 
            if (r.fastest_lap == 1 || r.fastest_lap === true || r.fastest_lap === 'true') { ctx.beginPath(); ctx.arc(badgeX, y + rowH/2, 12, 0, Math.PI*2); ctx.fillStyle = '#a855f7'; ctx.fill(); ctx.fillStyle='#fff'; ctx.font='bold 10px "Titillium Web"'; ctx.fillText("FL", badgeX, y + rowH/2 + 3); badgeX -= 30; }
             if (r.is_dotd == 1 || r.is_dotd === true || r.is_dotd === 'true') { ctx.beginPath(); ctx.arc(badgeX, y + rowH/2, 12, 0, Math.PI*2); ctx.fillStyle = '#eab308'; ctx.fill(); ctx.fillStyle='#000'; ctx.font='bold 9px "Titillium Web"'; ctx.fillText("DOTD", badgeX, y + rowH/2 + 3); }
            ctx.restore();
        }
    };
    
    // --- UPDATED QUALI RESULTS (Dynamic Gap Bar) ---
    const drawQualiResults = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const title = ev ? `QUALIFYING: ${ev.trackLocation.toUpperCase()}` : "QUALIFYING RESULTS";
        drawHeader(ctx, title, "Session Classification", w);

        if (!results || results.length === 0) return;
        const qResults = results.filter(r => r.quali_position > 0).sort((a,b) => a.quali_position - b.quali_position);
        if (qResults.length === 0) return;

        // --- PRE-CALCULATE GAPS ---
        const poleTimeStr = qResults[0].quali_time;
        const poleTime = parseTimeStr(poleTimeStr);
        let maxGap = 0;
        
        if (poleTime > 0) {
            qResults.forEach(r => {
                const t = parseTimeStr(r.quali_time);
                if (t > poleTime) {
                    const gap = t - poleTime;
                    if (gap > maxGap) maxGap = gap;
                }
            });
        }
        if (maxGap === 0) maxGap = 2; // Default scale if only 1 driver or no gaps

        const startY = 180; const leftX = 100; const leftW = 950; const rowH = 60; const gap = 15; const rightX = 1100; const rightW = 720;
        
        for (let i = 0; i < qResults.length; i++) {
            if (i >= 20) break;
            const r = qResults[i]; 
            const isTop10 = i < 10;
            const currentX = isTop10 ? leftX : rightX; 
            const currentW = isTop10 ? leftW : rightW;
            let currentY = startY + i * (rowH + gap); 
            if (!isTop10) { const offsetIdx = i - 10; currentY = startY + offsetIdx * (rowH + gap); }
            
            const skew = 20; 
            const rankW = isTop10 ? 80 : 60;
            
            ctx.fillStyle = i===0 ? '#a855f7' : '#333';
            ctx.beginPath(); ctx.moveTo(currentX + skew, currentY); ctx.lineTo(currentX + rankW + skew, currentY); ctx.lineTo(currentX + rankW, currentY + rowH); ctx.lineTo(currentX, currentY + rowH); ctx.fill();
            ctx.fillStyle = '#fff'; ctx.font = `900 ${isTop10 ? '32' : '26'}px "Titillium Web"`; ctx.textAlign = 'center'; ctx.fillText(r.quali_position.toString(), currentX + rankW/2 + skew/2, currentY + rowH * 0.7);

            const barX = currentX + rankW + 10; 
            const fullBarW = currentW - rankW - 120; 
            ctx.fillStyle = THEME_COLORS.cardBg; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + fullBarW + skew, currentY); ctx.lineTo(barX + fullBarW, currentY + rowH); ctx.lineTo(barX, currentY + rowH); ctx.fill();
            
            ctx.fillStyle = r.teamColor || '#666'; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + skew + 8, currentY); ctx.lineTo(barX + 8, currentY + rowH); ctx.lineTo(barX, currentY + rowH); ctx.fill();

            ctx.textAlign = 'left'; ctx.fillStyle = '#fff'; ctx.font = `bold ${isTop10 ? '28' : '22'}px "Titillium Web"`; ctx.fillText(String(r.username).toUpperCase(), barX + 30, currentY + rowH * 0.68);
            if (isTop10) { ctx.textAlign = 'right'; ctx.fillStyle = '#aaa'; ctx.font = 'bold 16px "Titillium Web"'; ctx.fillText(r.teamName?.toUpperCase() || "", barX + fullBarW - 20, currentY + rowH * 0.65); }

            const timeX = barX + fullBarW + 5; 
            const timeW = 150; 
            ctx.fillStyle = '#111'; 
            ctx.beginPath(); ctx.moveTo(timeX + skew, currentY); ctx.lineTo(timeX + timeW + skew, currentY); ctx.lineTo(timeX + timeW, currentY + rowH); ctx.lineTo(timeX, currentY + rowH); ctx.fill();

            // --- DYNAMIC GAP BAR ---
            if (i > 0 && poleTime > 0) {
                const myTime = parseTimeStr(r.quali_time);
                if (myTime > 0 && r.quali_status === 'FINISHED') {
                    const gap = myTime - poleTime;
                    const barWidth = (gap / maxGap) * (timeW + skew); 
                    ctx.save();
                    ctx.beginPath(); ctx.moveTo(timeX + skew, currentY); ctx.lineTo(timeX + timeW + skew, currentY); ctx.lineTo(timeX + timeW, currentY + rowH); ctx.lineTo(timeX, currentY + rowH); ctx.clip();
                    ctx.fillStyle = r.teamColor || '#666'; 
                    ctx.globalAlpha = 0.4;
                    ctx.fillRect(timeX, currentY, barWidth, rowH);
                    ctx.restore();
                }
            }

            ctx.fillStyle = r.quali_status === 'FINISHED' ? '#fff' : '#ef4444'; 
            ctx.textAlign = 'center'; ctx.font = 'bold 20px "Titillium Web"';
            let timeText = r.quali_time; 
            if (r.quali_status && r.quali_status !== 'FINISHED') timeText = r.quali_status.substring(0,3); 
            if (!timeText) timeText = "-";
            ctx.fillText(timeText, timeX + timeW/2 + skew/2, currentY + rowH * 0.68);
        }
    };
    
    const drawTeammateBattle = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "TEAMMATE BATTLES", "Points Contribution", w);
        if (teams.length === 0) return;
        const startY = 220; const rowH = 140; const gap = 30; const colW = 850; const colGap = 80; const startX = (w - (colW * 2 + colGap)) / 2;
        const teamData = teams.map(t => {
            const teamDrivers = drivers.filter(d => d.teamId === t.id && (!d.is_reserve || d.is_reserve == 0));
            const d1 = teamDrivers[0]; const d2 = teamDrivers[1];
            const p1Raw = d1 ? parseFloat(d1.totalPoints) : 0; const p2Raw = d2 ? parseFloat(d2.totalPoints) : 0;
            const v1 = Math.max(0, p1Raw); const v2 = Math.max(0, p2Raw); const totalVis = v1 + v2;
            const pct1 = totalVis > 0 ? (v1 / totalVis) : 0.5;
            return { team: t, d1, d2, p1: p1Raw, p2: p2Raw, pct1 };
        });
        const half = Math.ceil(teamData.length / 2);
        
        for (let i = 0; i < teamData.length; i++) {
            const item = teamData[i]; const col = i < half ? 0 : 1; const row = i % half;
            const x = startX + col * (colW + colGap); const y = startY + row * (rowH + gap);
            const tColor = item.team.color || item.team.teamColor || '#333'; 
            const textOnBarColor = getContrastColor(tColor);
            
            ctx.save();
            ctx.fillStyle = '#1e1e24'; ctx.fillRect(x, y, colW, rowH); 
            ctx.fillStyle = tColor; ctx.fillRect(x, y, 6, rowH); 
            
            let nameOff = 20;
            const tLogo = resolveTeamLogo(item.team);
            if(tLogo) { try { const l = await loadImage(tLogo); const s = Math.min(60/l.width, 60/l.height); ctx.drawImage(l, x + 20, y + 20, l.width*s, l.height*s); nameOff += (l.width*s) + 10; } catch {} }
            ctx.fillStyle = '#fff'; ctx.font = 'bold 20px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText(item.team.name.toUpperCase(), x + nameOff, y + 35);
            
            const barY = y + 60; const barH = 30; const barW = colW - 120; const barX = x + 100;
            const targetW1 = barW * item.pct1;

            ctx.fillStyle = tColor; ctx.fillRect(barX, barY, targetW1, barH);
            ctx.fillStyle = '#333'; ctx.fillRect(barX + targetW1, barY, barW - targetW1, barH);
            ctx.fillStyle = '#fff'; ctx.fillRect(barX + targetW1 - 1, barY, 2, barH);
            
            if (item.d1) {
                ctx.textAlign = 'left'; ctx.font = 'bold 18px "Titillium Web"'; ctx.fillStyle = '#fff'; ctx.fillText(String(item.d1.username).toUpperCase(), barX, barY + 50);
                let ptsX = barX + (targetW1 / 2); if (targetW1 < 50) ptsX = barX - 30; 
                ctx.textAlign = 'center'; ctx.fillStyle = (targetW1 < 50) ? '#fff' : textOnBarColor; ctx.fillText(Math.round(item.p1).toString(), ptsX, barY + 22);
                const isWinner = item.pct1 > 0.5; ctx.textAlign = 'left'; ctx.font = '900 24px "Titillium Web"'; ctx.fillStyle = isWinner ? THEME_COLORS.cyan : '#aaa'; ctx.fillText(Math.round(item.pct1 * 100) + "%", barX, barY + 70);
            }
            if (item.d2) {
                const w2 = barW - targetW1;
                ctx.textAlign = 'right'; ctx.font = 'bold 18px "Titillium Web"'; ctx.fillStyle = '#fff'; ctx.fillText(String(item.d2.username).toUpperCase(), barX + barW, barY + 50);
                let ptsX = barX + targetW1 + (w2 / 2); if (w2 < 50) ptsX = barX + barW + 30;
                ctx.textAlign = 'center'; ctx.fillStyle = '#fff'; ctx.fillText(Math.round(item.p2).toString(), ptsX, barY + 22);
                const isWinner = item.pct1 < 0.5; ctx.textAlign = 'right'; ctx.font = '900 24px "Titillium Web"'; ctx.fillStyle = isWinner ? THEME_COLORS.cyan : '#aaa'; ctx.fillText(Math.round((1-item.pct1) * 100) + "%", barX + barW, barY + 70);
            }
            ctx.restore();
        }
    };
    
    const drawHeadToHead = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        let e1, e2;
        if (h2hMode === 'DRIVER') { e1 = drivers.find(d => d.id === h2hId1); e2 = drivers.find(d => d.id === h2hId2); } else { e1 = teams.find(t => t.id === h2hId1); e2 = teams.find(t => t.id === h2hId2); }
        drawHeader(ctx, h2hMode === 'DRIVER' ? "HEAD TO HEAD" : "TEAM BATTLE", "", w);
        const cardW = 500; const cardH = 700; const cardY = 200; const gap = 400; const x1Final = (w/2) - (gap/2) - cardW; const x2Final = (w/2) + (gap/2);
        
        const drawCard = async (x: number, ent: any) => {
            if(!ent) return;
            ctx.save();
            const color = ent.teamColor || ent.color || '#333';
            ctx.fillStyle = '#fff'; ctx.fillRect(x, cardY, cardW, cardH);
            ctx.fillStyle = color; ctx.fillRect(x, cardY, cardW, 15);
            const imgUrl = h2hMode === 'DRIVER' ? (ent.avatarUrl || ent.teamLogo) : resolveTeamLogo(ent);
            if(imgUrl) { try { const img = await loadImage(imgUrl); const s = Math.min((cardW-40)/img.width, 350/img.height); ctx.drawImage(img, x+(cardW-img.width*s)/2, cardY+50, img.width*s, img.height*s); } catch {} }
            ctx.fillStyle='#000'; ctx.textAlign='center'; ctx.font='900 50px "Titillium Web"'; ctx.fillText((ent.username || ent.name).toUpperCase(), x+cardW/2, cardY+500);
            ctx.restore();
        };
        await drawCard(x1Final, e1); await drawCard(x2Final, e2);
        
        if(e1 && e2) {
             const statsY = cardY + 150;
             const labels = h2hMode === 'DRIVER' ? ["POINTS", "WINS", "PODIUMS", "RACES"] : ["POINTS", "WINS", "PODIUMS", "PPR"];
             let d1 = [], d2 = [];
             if (h2hMode === 'DRIVER') { d1 = [parseFloat(e1.totalPoints||0), parseInt(e1.wins||0), parseInt(e1.podiums||0), parseInt(e1.races||0)]; d2 = [parseFloat(e2.totalPoints||0), parseInt(e2.wins||0), parseInt(e2.podiums||0), parseInt(e2.races||0)]; } 
             else {
                 const getTeamStats = (tid: string) => { const teamDrivers = drivers.filter(d => d.teamId === tid); let pods = 0; let races = 0; teamDrivers.forEach(d => { pods += parseInt(d.podiums||0); races = Math.max(races, parseInt(d.races||0)); }); const pts = parseFloat(teams.find(t=>t.id===tid)?.totalPoints||0); const ppr = races > 0 ? (pts/races).toFixed(1) : "0.0"; return { pods, ppr }; };
                 const s1 = getTeamStats(e1.id); const s2 = getTeamStats(e2.id); d1 = [parseFloat(e1.totalPoints||0), parseInt(e1.wins||0), s1.pods, parseFloat(s1.ppr as string)]; d2 = [parseFloat(e2.totalPoints||0), parseInt(e2.wins||0), s2.pods, parseFloat(s2.ppr as string)];
             }
             ctx.save();
             for(let i=0; i<4; i++) {
                 const y = statsY + (i*120); ctx.fillStyle='#666'; ctx.font='bold 24px "Titillium Web"'; ctx.textAlign='center'; ctx.fillText(labels[i], w/2, y-20);
                 ctx.font='900 60px "Titillium Web"'; ctx.textAlign='right'; ctx.fillStyle = d1[i] > d2[i] ? THEME_COLORS.cyan : '#333'; ctx.fillText(d1[i].toString(), (w/2)-60, y+30);
                 ctx.textAlign='left'; ctx.fillStyle = d2[i] > d1[i] ? THEME_COLORS.cyan : '#333'; ctx.fillText(d2[i].toString(), (w/2)+60, y+30);
             }
             ctx.restore();
        }
    };
    
    const drawDriverStats = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        if (!selectedDriverId || !driverStatsData || !driverStatsData.user) { drawHeader(ctx, "DRIVER PROFILE", "Select a Driver", w); return; }
        const u = driverStatsData.user; const t = driverStatsData.team; const s = driverStatsData.stats; const color = t?.color || '#333';
        ctx.fillStyle = '#0f0f0f'; ctx.fillRect(0, 0, w, h); const grad = ctx.createLinearGradient(0, 0, w, h); grad.addColorStop(0, '#1a1a1a'); grad.addColorStop(1, '#000000'); ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h); ctx.fillStyle = color; ctx.fillRect(0, 0, 30, h);
        const leftW = 600;
        if (u.avatar_url) { try { const img = await loadImage(u.avatar_url); const size = 400; ctx.save(); ctx.beginPath(); ctx.arc(leftW/2 + 50, 300, size/2, 0, Math.PI * 2); ctx.clip(); ctx.drawImage(img, leftW/2 + 50 - size/2, 300 - size/2, size, size); ctx.restore(); ctx.strokeStyle = color; ctx.lineWidth = 10; ctx.beginPath(); ctx.arc(leftW/2 + 50, 300, size/2, 0, Math.PI * 2); ctx.stroke(); } catch {} }
        ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.font = '900 80px "Titillium Web"'; ctx.fillText(String(u.username).toUpperCase(), leftW/2 + 50, 600);
        if (t) { ctx.fillStyle = color; ctx.font = 'bold 40px "Titillium Web"'; ctx.fillText(t.name.toUpperCase(), leftW/2 + 50, 660); if (t.logo_url) { try { const l = await loadImage(t.logo_url); const ls = Math.min(150/l.width, 100/l.height); ctx.drawImage(l, leftW/2 + 50 - (l.width*ls)/2, 700, l.width*ls, l.height*ls); } catch {} } }
        const rightX = 700; const rightW = w - rightX - 50;
        ctx.textAlign = 'left'; ctx.fillStyle = '#fff'; ctx.font = '900 100px "Titillium Web"'; ctx.fillText("SEASON STATS", rightX, 120); ctx.fillStyle = '#666'; ctx.font = 'bold 40px "Titillium Web"'; ctx.fillText("PERFORMANCE OVERVIEW", rightX + 5, 170);
        const statBoxW = (rightW - 40) / 2; const statBoxH = 180;
        const drawStatBox = (title: string, value: string, sub: string, bx: number, by: number) => { ctx.fillStyle = '#1e1e24'; ctx.fillRect(bx, by, statBoxW, statBoxH); ctx.fillStyle = color; ctx.fillRect(bx, by, 10, statBoxH); ctx.textAlign = 'left'; ctx.fillStyle = '#888'; ctx.font = 'bold 24px "Titillium Web"'; ctx.fillText(title.toUpperCase(), bx + 30, by + 40); ctx.fillStyle = '#fff'; ctx.font = '900 80px "Titillium Web"'; ctx.fillText(value, bx + 30, by + 130); ctx.textAlign = 'right'; ctx.fillStyle = '#666'; ctx.font = 'bold 20px "Titillium Web"'; ctx.fillText(sub, bx + statBoxW - 20, by + 160); };
        drawStatBox("Rank", s.best_pos ? `#${s.best_pos}` : '-', "Best Finish", rightX, 250); drawStatBox("Points", Math.round(s.points).toString(), "Total", rightX + statBoxW + 40, 250); drawStatBox("Podiums", s.podiums.toString(), "Top 3", rightX, 450); drawStatBox("Wins", s.wins.toString(), "P1", rightX + statBoxW + 40, 450);
        const graphY = 700; const graphH = 300; const graphW = rightW;
        ctx.fillStyle = '#111'; ctx.fillRect(rightX, graphY, graphW, graphH);
        const hist = driverStatsData.history || []; const chartData = [...hist].reverse(); 
        if (chartData.length > 1) { ctx.strokeStyle = '#333'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(rightX, graphY + graphH); ctx.lineTo(rightX + graphW, graphY + graphH); ctx.lineTo(rightX + graphW, graphY); ctx.stroke(); ctx.strokeStyle = color; ctx.lineWidth = 5; ctx.beginPath(); const stepX = graphW / (chartData.length - 1); chartData.forEach((pt: any, i: number) => { const pos = parseInt(pt.position); const px = rightX + (i * stepX); const py = graphY + ((pos / 20) * (graphH - 40)) + 20; if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py); ctx.save(); ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI*2); ctx.fill(); ctx.restore(); ctx.fillStyle = '#aaa'; ctx.font = 'bold 16px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(`P${pos}`, px, py - 15); }); ctx.stroke(); ctx.fillStyle = '#666'; ctx.textAlign = 'center'; ctx.font = 'bold 20px "Titillium Web"'; ctx.fillText("RECENT RACE POSITIONS", rightX + graphW/2, graphY + graphH + 30); } else { ctx.fillStyle = '#444'; ctx.textAlign = 'center'; ctx.font = 'italic 30px "Titillium Web"'; ctx.fillText("Not enough race data for graph", rightX + graphW/2, graphY + graphH/2); }
    };
    
    // Stub functions for others
    const drawLeagueStats = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "LEAGUE RANKINGS", "Season Statistics Overview", w);
        if (!leagueStatsData) return;

        const categories = [
            { title: "FASTEST LAPS", data: leagueStatsData.fastest_lap, icon: 'Timer', unit: '' },
            { title: "CLEAN RACES", data: leagueStatsData.clean_races, icon: 'Flag', unit: '' },
            { title: "PENALTY KINGS", data: leagueStatsData.penalties, icon: 'Activity', unit: 's' },
            { title: "PODIUMS", data: leagueStatsData.podiums, icon: 'Trophy', unit: '' },
            { title: "HARD CHARGERS", data: leagueStatsData.hard_charger, icon: 'Activity', unit: ' pos' },
            { title: "CONSISTENCY", data: leagueStatsData.consistency, icon: 'BarChart2', unit: ' avg' }
        ];

        const marginX = 80;
        const startY = 180;
        const cardGap = 30;
        const cardW = (w - (marginX * 2) - (cardGap * 2)) / 3;
        const cardH = 400;

        categories.forEach((cat, idx) => {
            const col = idx % 3;
            const row = Math.floor(idx / 3);
            const x = marginX + col * (cardW + cardGap);
            const y = startY + row * (cardH + cardGap);

            // Card BG
            ctx.fillStyle = '#1e1e24';
            roundRect(ctx, x, y, cardW, cardH, 10);

            // Header
            ctx.fillStyle = THEME_COLORS.cyan;
            ctx.fillRect(x, y, cardW, 40);
            ctx.fillStyle = '#000';
            ctx.font = '900 20px "Titillium Web"';
            ctx.textAlign = 'center';
            ctx.fillText(cat.title, x + cardW / 2, y + 28);

            // List
            const listData = cat.data || [];
            listData.slice(0, 5).forEach((item: any, i: number) => {
                const rowY = y + 60 + (i * 65);
                
                // Row BG
                ctx.fillStyle = 'rgba(255,255,255,0.05)';
                roundRect(ctx, x + 10, rowY, cardW - 20, 55, 4);

                // Team Color Strip
                ctx.fillStyle = item.team_color || '#666';
                ctx.fillRect(x + 10, rowY + 5, 4, 45);

                // Name
                ctx.fillStyle = '#fff';
                ctx.font = 'bold 18px "Titillium Web"';
                ctx.textAlign = 'left';
                ctx.fillText(String(item.username).toUpperCase(), x + 25, rowY + 35);

                // Value
                ctx.fillStyle = THEME_COLORS.cyan;
                ctx.font = '900 22px "Titillium Web"';
                ctx.textAlign = 'right';
                ctx.fillText(item.value + cat.unit, x + cardW - 25, rowY + 35);
            });
        });
    };

    // --- CHART DRAWING (Driver & Team WM Verlauf) ---
    const drawChartGraphic = async (ctx: CanvasRenderingContext2D, w: number, h: number, chartData: any[], title: string, subtitle: string) => {
        drawHeader(ctx, title, subtitle, w);

        if (!chartData || chartData.length === 0) {
            ctx.fillStyle = '#666';
            ctx.font = 'bold 36px "Titillium Web"';
            ctx.textAlign = 'center';
            ctx.fillText('Keine Chart-Daten vorhanden', w / 2, h / 2);
            return;
        }

        // Sort by final points descending
        const sorted = [...chartData].sort((a, b) => {
            const lastA = a.data && a.data.length > 0 ? a.data[a.data.length - 1].points : 0;
            const lastB = b.data && b.data.length > 0 ? b.data[b.data.length - 1].points : 0;
            return lastB - lastA;
        });

        // Only show top 10
        const series = sorted.slice(0, 10);

        // Calculate scales
        let maxPts = 0, maxRnd = 0;
        chartData.forEach(s => {
            if (s.data) s.data.forEach((p: any) => {
                if (p.points > maxPts) maxPts = p.points;
                if (p.round > maxRnd) maxRnd = p.round;
            });
        });
        if (maxRnd < 1) maxRnd = 1;
        if (maxPts < 10) maxPts = 10;
        const niceStep = maxPts <= 50 ? 10 : maxPts <= 100 ? 20 : maxPts <= 250 ? 50 : 100;
        maxPts = Math.ceil(maxPts * 1.1 / niceStep) * niceStep;

        const chartLeft = 140, chartRight = w - 340, chartTop = 200, chartBottom = h - 100;
        const chartW = chartRight - chartLeft, chartH = chartBottom - chartTop;

        const xS = (r: number) => chartLeft + (r / maxRnd) * chartW;
        const yS = (p: number) => chartBottom - (p / maxPts) * chartH;

        // Draw chart background
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.fillRect(chartLeft - 10, chartTop - 10, chartW + 20, chartH + 20);

        // Grid lines Y
        const gridCount = 5;
        for (let i = 0; i <= gridCount; i++) {
            const val = Math.round((maxPts / gridCount) * i);
            const y = yS(val);
            ctx.strokeStyle = 'rgba(0,0,0,0.08)';
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(chartLeft, y); ctx.lineTo(chartRight, y); ctx.stroke();
            ctx.fillStyle = '#555';
            ctx.font = 'bold 22px "Titillium Web"';
            ctx.textAlign = 'right';
            ctx.fillText(String(val), chartLeft - 15, y + 7);
        }

        // Grid lines X (rounds)
        for (let r = 1; r <= maxRnd; r++) {
            const x = xS(r);
            ctx.strokeStyle = 'rgba(0,0,0,0.06)';
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(x, chartTop); ctx.lineTo(x, chartBottom); ctx.stroke();
            ctx.fillStyle = '#555';
            ctx.font = 'bold 20px "Titillium Web"';
            ctx.textAlign = 'center';
            ctx.fillText(`R${r}`, x, chartBottom + 30);
        }

        // Draw lines
        series.forEach(s => {
            if (!s.data || s.data.length < 2) return;
            const color = s.color || '#ffffff';

            // Area fill
            ctx.beginPath();
            ctx.moveTo(xS(s.data[0].round), yS(s.data[0].points));
            s.data.slice(1).forEach((p: any) => ctx.lineTo(xS(p.round), yS(p.points)));
            ctx.lineTo(xS(s.data[s.data.length - 1].round), chartBottom);
            ctx.lineTo(xS(s.data[0].round), chartBottom);
            ctx.closePath();
            ctx.fillStyle = color + '20';
            ctx.fill();

            // Line shadow for contrast
            ctx.beginPath();
            s.data.forEach((p: any, i: number) => {
                const x = xS(p.round), y = yS(p.points);
                i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            });
            ctx.strokeStyle = 'rgba(0,0,0,0.15)';
            ctx.lineWidth = 7;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.stroke();

            // Line
            ctx.beginPath();
            s.data.forEach((p: any, i: number) => {
                const x = xS(p.round), y = yS(p.points);
                i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            });
            ctx.strokeStyle = color;
            ctx.lineWidth = 4;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.stroke();

            // Points
            s.data.forEach((p: any) => {
                const x = xS(p.round), y = yS(p.points);
                ctx.beginPath();
                ctx.arc(x, y, 5, 0, Math.PI * 2);
                ctx.fillStyle = '#f8f8f8';
                ctx.fill();
                ctx.strokeStyle = color;
                ctx.lineWidth = 3;
                ctx.stroke();
            });
        });

        // Legend (right side)
        const legendX = chartRight + 30;
        let legendY = chartTop + 10;
        ctx.textAlign = 'left';

        series.forEach((s, idx) => {
            const y = legendY + idx * 45;
            const pts = s.data && s.data.length > 0 ? s.data[s.data.length - 1].points : 0;
            const color = s.color || '#333333';

            // Color dot
            ctx.beginPath();
            ctx.arc(legendX + 8, y + 8, 6, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();

            // Name
            ctx.fillStyle = '#1a1a1a';
            ctx.font = 'bold 22px "Titillium Web"';
            ctx.fillText(s.name, legendX + 24, y + 15);

            // Points
            ctx.fillStyle = '#444';
            ctx.font = '900 22px "Titillium Web"';
            ctx.textAlign = 'right';
            ctx.fillText(String(pts), w - 60, y + 15);
            ctx.textAlign = 'left';
        });
    };

    const drawDriverChart = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        await drawChartGraphic(ctx, w, h, chartDataDriver, 'FAHRER-WM VERLAUF', 'Championship Points Progression');
    };

    const drawTeamChart = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        await drawChartGraphic(ctx, w, h, chartDataTeam, 'KONSTRUKTEURS-WM VERLAUF', 'Constructor Championship Progression');
    };

    const drawCalendar = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {};
    
    // --- MAIN RENDERER ---
    const renderCanvas = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const W = 1920;
        const H = 1080;
        canvas.width = W;
        canvas.height = H;

        // Background
        if (isTransparent) {
            ctx.clearRect(0, 0, W, H);
        } else if (bgImage) {
            try {
                const img = await loadImage(bgImage);
                // Draw cover
                const ratio = Math.max(W / img.width, H / img.height);
                const centerShift_x = (W - img.width * ratio) / 2;
                const centerShift_y = (H - img.height * ratio) / 2;
                ctx.drawImage(img, 0, 0, img.width, img.height, centerShift_x, centerShift_y, img.width * ratio, img.height * ratio);
            } catch (e) {
                drawCleanF1Background(ctx, W, H);
            }
        } else {
            drawCleanF1Background(ctx, W, H);
        }

        try {
            switch (activeType) {
                case 'DRIVER_STANDINGS': await drawDriverStandings(ctx, W, H); break;
                case 'TEAM_STANDINGS': await drawTeamStandings(ctx, W, H); break;
                case 'RACE_RESULTS': await drawRaceResults(ctx, W, H); break;
                case 'RACE_RESULTS_DETAILED': await drawDetailedRaceResults(ctx, W, H); break;
                case 'QUALI_RESULTS': await drawQualiResults(ctx, W, H); break;
                case 'STARTING_GRID': await drawStartingGrid(ctx, W, H); break;
                case 'CALENDAR': await drawCalendar(ctx, W, H); break;
                case 'SEASON_STATS': await drawSeasonStats(ctx, W, H); break;
                case 'HEAD_TO_HEAD': await drawHeadToHead(ctx, W, H); break;
                case 'TEAMMATE_BATTLE': await drawTeammateBattle(ctx, W, H); break;
                case 'DRIVER_STATS': await drawDriverStats(ctx, W, H); break;
                case 'LEAGUE_STATS': await drawLeagueStats(ctx, W, H); break;
                case 'DRIVER_CHART': await drawDriverChart(ctx, W, H); break;
                case 'TEAM_CHART': await drawTeamChart(ctx, W, H); break;
            }
        } catch (e) {
            // console.error("Render Error:", e);
        }
    };

    // Trigger render
    useEffect(() => {
        const t = setTimeout(renderCanvas, 200);
        return () => clearTimeout(t);
    }, [activeType, isTransparent, drivers, teams, events, results, gridEntries, eventPenalties, selectedEventId, selectedSession, matrix, h2hMode, h2hId1, h2hId2, driverStatsData, bgImage, leagueStatsData, chartDataDriver, chartDataTeam]);

    const handleDownload = () => {
        const c = canvasRef.current; if(!c) return;
        const l = document.createElement('a');
        l.download = `Caster_${activeType}.png`;
        l.href = c.toDataURL('image/png');
        l.click();
    };

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic">Caster Graphics (Transparent)</h1>
                    <p className="text-gray-400 text-sm">OBS/Stream Overlay Generator (1080p Transparent)</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={loadData} icon={RefreshCw}>Refresh Data</Button>
                    <Button variant="secondary" onClick={toggleFullscreen} icon={isFullscreen ? Minimize : Maximize}>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</Button>
                    <Button onClick={handleDownload} icon={Download}>Download PNG</Button>
                </div>
            </div>

            <Card className="bg-gray-800 border-gray-700">
                <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => setActiveType('DRIVER_STANDINGS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'DRIVER_STANDINGS' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><Trophy size={14}/> Driver St.</button>
                        <button onClick={() => setActiveType('TEAM_STANDINGS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'TEAM_STANDINGS' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><Grid size={14}/> Team St.</button>
                        <button onClick={() => setActiveType('STARTING_GRID')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'STARTING_GRID' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><List size={14}/> Grid</button>
                        <button onClick={() => setActiveType('RACE_RESULTS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'RACE_RESULTS' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><Flag size={14}/> Race Res.</button>
                        <button onClick={() => setActiveType('RACE_RESULTS_DETAILED')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'RACE_RESULTS_DETAILED' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><FileText size={14}/> Detailed</button>
                        <button onClick={() => setActiveType('QUALI_RESULTS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'QUALI_RESULTS' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><Timer size={14}/> Quali</button>
                        <button onClick={() => setActiveType('SEASON_STATS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'SEASON_STATS' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><BarChart2 size={14}/> Ext. Stats</button>
                        <button onClick={() => setActiveType('HEAD_TO_HEAD')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'HEAD_TO_HEAD' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><X size={14}/> H2H</button>
                        <button onClick={() => setActiveType('TEAMMATE_BATTLE')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'TEAMMATE_BATTLE' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><Activity size={14}/> Battle</button>
                        <button onClick={() => setActiveType('DRIVER_STATS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'DRIVER_STATS' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><User size={14}/> Driver Stats</button>
                        <button onClick={() => setActiveType('LEAGUE_STATS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'LEAGUE_STATS' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><BarChart2 size={14}/> League Stats</button>
                        <button onClick={() => setActiveType('DRIVER_CHART')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'DRIVER_CHART' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><TrendingUp size={14}/> Driver Chart</button>
                        <button onClick={() => setActiveType('TEAM_CHART')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeType === 'TEAM_CHART' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400'}`}><TrendingUp size={14}/> Team Chart</button>
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                         {/* BG Upload */}
                        <div className="flex items-center gap-1 mr-2">
                             <input 
                                 type="file" 
                                 ref={fileInputRef} 
                                 className="hidden" 
                                 accept="image/*" 
                                 onChange={e => {
                                     const file = e.target.files?.[0];
                                     if(file) {
                                         const reader = new FileReader();
                                         reader.onload = (ev) => setBgImage(ev.target?.result as string);
                                         reader.readAsDataURL(file);
                                     }
                                 }}
                             />
                             <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} className="text-xs h-8">
                                 <ImageIcon size={14} className="mr-1"/> BG
                             </Button>
                             {bgImage && (
                                 <button onClick={() => setBgImage(null)} className="p-2 bg-red-900/50 rounded text-red-500 hover:text-white h-8 w-8 flex items-center justify-center">
                                     <X size={14}/>
                                 </button>
                             )}
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer bg-gray-900 px-3 py-2 rounded border border-gray-600 text-white text-xs font-bold uppercase hover:bg-gray-800 transition-colors">
                            <input 
                                type="checkbox" 
                                checked={isTransparent} 
                                onChange={e => setIsTransparent(e.target.checked)} 
                                className="w-4 h-4 text-f1-red rounded focus:ring-0 cursor-pointer"
                            />
                            <Layers size={14}/> Transparent
                        </label>
                    </div>
                </div>
                
                {/* Event Selector for Grid/Results */}
                {(activeType === 'RACE_RESULTS' || activeType === 'RACE_RESULTS_DETAILED' || activeType === 'STARTING_GRID' || activeType === 'QUALI_RESULTS') && (
                    <div className="mb-4 flex flex-wrap gap-4 items-end">
                         <div>
                            <label className="text-[10px] uppercase text-gray-500 font-bold mr-2 block mb-1">Event:</label>
                            <select className="bg-gray-900 border border-gray-600 text-white rounded px-2 py-1 text-sm min-w-[200px]" value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)}>
                                {events.map(e => <option key={e.id} value={e.id}>{e.trackLocation} (R{e.roundNumber})</option>)}
                            </select>
                         </div>

                         {/* Session Selector for Sprint Weekends */}
                         {events.find(e => e.id === selectedEventId)?.isSprint && (
                             <div>
                                 <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Session:</label>
                                 <div className="flex gap-1">
                                     <button 
                                         onClick={() => setSelectedSession('RACE')} 
                                         className={`px-3 py-1 rounded text-xs border font-bold uppercase transition-colors ${selectedSession==='RACE'?'bg-f1-red text-white border-f1-red':'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'}`}
                                     >
                                         Hauptrennen
                                     </button>
                                     <button 
                                         onClick={() => setSelectedSession('SPRINT')} 
                                         className={`px-3 py-1 rounded text-xs border font-bold uppercase transition-colors ${selectedSession==='SPRINT'?'bg-f1-red text-white border-f1-red':'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'}`}
                                     >
                                         Sprint
                                     </button>
                                 </div>
                             </div>
                         )}
                    </div>
                )}
                
                {/* Driver Selector for Stats */}
                {activeType === 'DRIVER_STATS' && (
                    <div className="mb-4">
                         <label className="text-[10px] uppercase text-gray-500 font-bold mr-2">Fahrer:</label>
                         <select className="bg-gray-900 border border-gray-600 text-white rounded px-2 py-1 text-sm" value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value)}>
                            <option value="">Wählen...</option>
                            {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                         </select>
                    </div>
                )}
                
                {/* H2H Controls */}
                {activeType === 'HEAD_TO_HEAD' && (
                    <div className="flex flex-wrap gap-4 mb-4 items-end bg-gray-900/50 p-3 rounded border border-gray-800">
                        <div>
                            <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Mode</label>
                            <div className="flex gap-1">
                                <button onClick={() => setH2HMode('DRIVER')} className={`px-3 py-1 rounded text-xs border ${h2hMode==='DRIVER'?'bg-blue-600 text-white':'bg-gray-800 text-gray-400'}`}>Fahrer</button>
                                <button onClick={() => setH2HMode('TEAM')} className={`px-3 py-1 rounded text-xs border ${h2hMode==='TEAM'?'bg-blue-600 text-white':'bg-gray-800 text-gray-400'}`}>Teams</button>
                            </div>
                        </div>
                        <div className="flex-1 min-w-[150px]">
                            <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Links</label>
                            <select className="w-full bg-gray-800 border border-gray-600 text-white rounded px-2 py-1 text-sm" value={h2hId1} onChange={e => setH2HId1(e.target.value)}>
                                <option value="">Wählen...</option>
                                {h2hMode === 'DRIVER' ? drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>) : teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                        <div className="flex-1 min-w-[150px]">
                            <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Rechts</label>
                            <select className="w-full bg-gray-800 border border-gray-600 text-white rounded px-2 py-1 text-sm" value={h2hId2} onChange={e => setH2HId2(e.target.value)}>
                                <option value="">Wählen...</option>
                                {h2hMode === 'DRIVER' ? drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>) : teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    </div>
                )}

                <div ref={containerRef} className={`relative border border-gray-600 rounded overflow-hidden flex items-center justify-center p-4 ${isFullscreen ? 'bg-black p-0 border-0' : "bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"}`}>
                    <canvas 
                        ref={canvasRef} 
                        style={{ 
                            maxWidth: '100%', 
                            maxHeight: isFullscreen ? '100vh' : '600px', 
                            boxShadow: isFullscreen ? 'none' : '0 0 20px rgba(0,0,0,0.5)',
                            width: isFullscreen ? '100%' : 'auto',
                            height: isFullscreen ? '100%' : 'auto',
                            objectFit: 'contain'
                        }} 
                    />

                    {isFullscreen && (
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-wrap justify-center gap-2 p-3 bg-black/60 backdrop-blur-md rounded-xl border border-white/10 opacity-0 hover:opacity-100 transition-opacity duration-300 max-w-[90vw]">
                            <button onClick={() => setActiveType('DRIVER_STANDINGS')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'DRIVER_STANDINGS' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><Trophy size={16}/><span>Drivers</span></button>
                            <button onClick={() => setActiveType('TEAM_STANDINGS')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'TEAM_STANDINGS' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><Grid size={16}/><span>Teams</span></button>
                            <button onClick={() => setActiveType('STARTING_GRID')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'STARTING_GRID' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><List size={16}/><span>Grid</span></button>
                            <button onClick={() => setActiveType('RACE_RESULTS')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'RACE_RESULTS' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><Flag size={16}/><span>Race</span></button>
                            <button onClick={() => setActiveType('RACE_RESULTS_DETAILED')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'RACE_RESULTS_DETAILED' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><FileText size={16}/><span>Detailed</span></button>
                            <button onClick={() => setActiveType('QUALI_RESULTS')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'QUALI_RESULTS' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><Timer size={16}/><span>Quali</span></button>
                            <button onClick={() => setActiveType('SEASON_STATS')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'SEASON_STATS' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><BarChart2 size={16}/><span>Stats</span></button>
                            <button onClick={() => setActiveType('HEAD_TO_HEAD')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'HEAD_TO_HEAD' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><X size={16}/><span>H2H</span></button>
                            <button onClick={() => setActiveType('TEAMMATE_BATTLE')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'TEAMMATE_BATTLE' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><Activity size={16}/><span>Battle</span></button>
                            <button onClick={() => setActiveType('DRIVER_STATS')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'DRIVER_STATS' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><User size={16}/><span>Driver</span></button>
                            <button onClick={() => setActiveType('LEAGUE_STATS')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'LEAGUE_STATS' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><BarChart2 size={16}/><span>League</span></button>
                            <button onClick={() => setActiveType('DRIVER_CHART')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'DRIVER_CHART' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><TrendingUp size={16}/><span>D-Chart</span></button>
                            <button onClick={() => setActiveType('TEAM_CHART')} className={`p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] ${activeType === 'TEAM_CHART' ? 'bg-f1-red text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700'}`}><TrendingUp size={16}/><span>T-Chart</span></button>
                            <div className="w-px bg-white/10 mx-1 self-stretch" />
                            <button onClick={toggleFullscreen} className="p-2 rounded font-bold text-[10px] uppercase flex flex-col items-center gap-1 min-w-[60px] bg-gray-800/50 text-gray-300 hover:bg-red-600 hover:text-white"><Minimize size={16}/><span>Exit</span></button>
                        </div>
                    )}
                </div>
                <div className="text-center text-xs text-gray-500 mt-2">1920x1080 Native Resolution - Right click image to copy</div>
            </Card>
        </div>
    );
};
